import { useState } from 'react';
import {
  durationOptions,
  regionOptions,
  sortOptions,
  yearOptions,
  type SortKey,
} from '@/lib/queries';

type FilterState = {
  region: string;
  year: string;
  duration: string;
  sort: SortKey;
};

type MovieFilterBarProps = {
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  resultCount: number;
};

type ChipGroupProps = {
  label: string;
  options: string[];
  active: string;
  onSelect: (value: string) => void;
};

function ChipGroup({ label, options, active, onSelect }: ChipGroupProps) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="mt-2 w-12 shrink-0 text-xs text-foreground-600">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors ${
              active === option
                ? 'bg-primary-500 text-background-50'
                : 'border border-background-800 bg-background-900 text-foreground-400 hover:border-background-600 hover:text-foreground-100'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MovieFilterBar({ value, onChange, onReset, resultCount }: MovieFilterBarProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-background-800 bg-background-900">
      <div className="flex items-center justify-between gap-3 border-b border-background-800 px-4 py-3 md:hidden">
        <div className="flex items-center gap-2 text-xs text-foreground-400">
          <i className="ri-filter-3-line text-base text-accent-500" />
          共 {resultCount} 部影片
        </div>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-700 px-3 py-1.5 text-xs text-foreground-300"
        >
          {open ? '收起筛选' : '筛选与排序'}
          <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div className={`${open ? 'block' : 'hidden'} p-4 md:block md:p-5`}>
        <div className="space-y-4">
          <ChipGroup
            label="地区"
            options={regionOptions}
            active={value.region}
            onSelect={(region) => onChange({ ...value, region })}
          />
          <ChipGroup
            label="年份"
            options={yearOptions}
            active={value.year}
            onSelect={(year) => onChange({ ...value, year })}
          />
          <ChipGroup
            label="片长"
            options={durationOptions}
            active={value.duration}
            onSelect={(duration) => onChange({ ...value, duration })}
          />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-800 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 text-xs text-foreground-600">
              <i className="ri-sort-desc text-sm" />
              排序
            </span>
            <div className="flex gap-1 rounded-md border border-background-800 bg-background-950 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onChange({ ...value, sort: option.key })}
                  className={`cursor-pointer whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${
                    value.sort === option.key
                      ? 'bg-accent-500 font-medium text-background-950'
                      : 'text-foreground-400 hover:text-foreground-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onReset}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-xs text-foreground-500 transition-colors hover:text-accent-400"
          >
            <i className="ri-refresh-line text-sm" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}